import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import confetti from "canvas-confetti";
import { CreditCard, Package, Receipt } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useWallet } from "../hooks/useWallet";
import { useCheckout } from "../contexts/CheckoutContext";
import GlassCard from "../components/GlassCard";
import PlanCard from "../components/PlanCard";
import PackageCard from "../components/PackageCard";
import StatusPill from "../components/StatusPill";
import WalletSyncFooter from "../components/WalletSyncFooter";
import {
  listPackages,
  listPlans,
  listPaymentsHistory,
  formatPrice,
  getCheckoutStatus,
  getStripeConfig,
} from "../lib/billing";
import { cn } from "../lib/utils";

const TABS = [
  { key: "plan", icon: CreditCard },
  { key: "packages", icon: Package },
  { key: "history", icon: Receipt },
];

// Module-level dedup set for Stripe return-page UX. Survives React.StrictMode's
// dev-only mount → cleanup → remount cycle so the success toast/confetti fires
// exactly once per session_id, regardless of how many times the effect runs.
const _processedSessions = new Set();

function formatDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString([], { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return iso;
  }
}

export default function Billing() {
  const { t } = useTranslation();
  const { user, refresh: refreshAuth } = useAuth();
  const { plan, lastUpdated, refresh: refreshWallet } = useWallet();
  const { openCheckout } = useCheckout();

  const [tab, setTab] = useState("plan");
  const [packages, setPackages] = useState([]);
  const [plans, setPlans] = useState([]);
  const [payments, setPayments] = useState({ items: [], total: 0, page: 1, page_size: 20 });
  const [loading, setLoading] = useState(false);
  const [stripeMode, setStripeMode] = useState(null); // "test" | "live" | null while loading

  // ----- Phase 14: fetch Stripe mode once per mount -------------------------
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const res = await getStripeConfig();
      if (!cancelled && res.ok) setStripeMode(res.data?.mode ?? null);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // ----- Phase 9: Stripe redirect-back handling -----------------------------
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const success = searchParams.get("success") === "true";
    const canceled = searchParams.get("canceled") === "true";
    const sessionId = searchParams.get("session_id");

    // Cancel path — one-shot toast + query cleanup.
    if (canceled) {
      const key = `canceled:${searchParams.toString()}`;
      if (_processedSessions.has(key)) return;
      _processedSessions.add(key);
      toast.info(t("checkout.canceled"));
      const next = new URLSearchParams(searchParams);
      next.delete("canceled");
      setSearchParams(next, { replace: true });
      return;
    }

    if (!success || !sessionId) return;
    // Dedup by session_id at module scope so React.StrictMode's dev double-mount
    // can't kill the terminal UX (previously used a `let live=true` closure
    // guard that was toggled by cleanup — see iteration_10.json RCA).
    if (_processedSessions.has(sessionId)) return;
    _processedSessions.add(sessionId);

    (async () => {
      // Poll up to 8x1s. Server-side inline reconciliation usually resolves on
      // the first call; the extra tries accommodate a lagging webhook.
      let credited = false;
      for (let i = 0; i < 8; i += 1) {
        const res = await getCheckoutStatus(sessionId);
        if (res.ok && res.data?.credited) {
          credited = true;
          break;
        }
        await new Promise((r) => setTimeout(r, 900));
      }
      // From here on, we NEVER short-circuit on unmount — toast + confetti +
      // setSearchParams are safe to call on unmounted components (sonner and
      // canvas-confetti are DOM-global; useSearchParams no-ops post-unmount).
      if (credited) {
        toast.success(t("checkout.success"), {
          description: t("checkout.successBody"),
          style: {
            border: "1px solid rgba(212,175,55,0.55)",
            background: "rgba(12,12,14,0.96)",
            color: "#F4D77A",
          },
        });
        try {
          confetti({
            particleCount: 120,
            spread: 90,
            colors: ["#D4AF37", "#F4D77A", "#22D3EE", "#FFFFFF"],
            origin: { x: 0.5, y: 0.35 },
            disableForReducedMotion: true,
          });
        } catch {
          /* ignore in non-DOM environments */
        }
        await refreshAuth();
        await refreshWallet?.();
        setTab("history");
      } else {
        toast.warning(t("checkout.pending"));
      }
      const next = new URLSearchParams(searchParams);
      next.delete("success");
      next.delete("session_id");
      setSearchParams(next, { replace: true });
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    (async () => {
      const [pkg, pl] = await Promise.all([listPackages(), listPlans()]);
      if (pkg.ok) setPackages(pkg.data);
      if (pl.ok) setPlans(pl.data);
    })();
  }, []);

  useEffect(() => {
    if (tab !== "history") return;
    let live = true;
    (async () => {
      setLoading(true);
      const res = await listPaymentsHistory(1, 20);
      if (!live) return;
      setLoading(false);
      if (res.ok) setPayments(res.data);
    })();
    return () => {
      live = false;
    };
  }, [tab, user?.id, lastUpdated]);

  const onChoosePlan = (id) => {
    if (id === plan) return;
    if (id === "pro") openCheckout({ kind: "subscription", planId: "pro" });
    else openCheckout({ kind: "subscription", planId: "free" });
  };

  const onBuyPackage = (id) => {
    openCheckout({ kind: "recharge", packageId: id });
  };

  const tabClass = useMemo(
    () => (k) =>
      cn(
        "inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-medium transition-colors",
        tab === k
          ? "bg-white/[0.06] text-white border border-white/10"
          : "text-white/55 hover:text-white/80",
      ),
    [tab],
  );

  return (
    <section className="grid gap-6" data-testid="billing-page">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl text-white tracking-tight">
            {t("billing.title")}
          </h1>
          <p className="mt-1 text-sm text-white/55 max-w-lg">{t("billing.subtitle")}</p>
        </div>
        <span
          className={cn(
            "inline-flex items-center rounded-full border px-3 py-1 text-[11px] uppercase tracking-wider",
            stripeMode === "live"
              ? "border-[#D4AF37]/40 bg-[#D4AF37]/[0.06] text-[#F4D77A]"
              : "border-emerald-500/30 bg-emerald-500/[0.05] text-emerald-200",
          )}
          data-testid="billing-stripe-badge"
          data-stripe-mode={stripeMode || "unknown"}
        >
          {stripeMode === "live"
            ? t("billing.stripe.live_mode_badge")
            : t("billing.stripe.test_mode_badge")}
        </span>
      </header>

      <nav className="flex items-center gap-1 border-b border-white/5 pb-3">
        {TABS.map((tk) => {
          const Icon = tk.icon;
          return (
            <button
              key={tk.key}
              onClick={() => setTab(tk.key)}
              className={tabClass(tk.key)}
              data-testid={`billing-tab-${tk.key}`}
              aria-pressed={tab === tk.key}
            >
              <Icon className="h-4 w-4" />
              {t(`billing.tab.${tk.key}`)}
            </button>
          );
        })}
      </nav>

      {tab === "plan" && (
        <div className="grid gap-5 sm:grid-cols-2" data-testid="billing-plan-grid">
          {plans.map((p) => (
            <PlanCard
              key={p.id}
              plan={p}
              isCurrent={plan === p.id}
              onChoose={() => onChoosePlan(p.id)}
            />
          ))}
        </div>
      )}

      {tab === "packages" && (
        <div className="grid gap-5 sm:grid-cols-3" data-testid="billing-packages-grid">
          {packages.map((p) => (
            <PackageCard key={p.id} pkg={p} onBuy={() => onBuyPackage(p.id)} />
          ))}
        </div>
      )}

      {tab === "history" && (
        <GlassCard className="p-0 overflow-hidden" data-testid="billing-history">
          {loading ? (
            <div className="p-6 text-sm text-white/55">{t("common.loading")}</div>
          ) : payments.items.length === 0 ? (
            <div className="p-10 text-center text-sm text-white/55" data-testid="billing-history-empty">
              {t("billing.historyEmpty")}
            </div>
          ) : (
            <ul className="divide-y divide-white/5">
              {payments.items.map((p) => (
                <li
                  key={p.id}
                  className="flex flex-wrap items-center gap-4 px-5 py-4"
                  data-testid={`billing-history-row-${p.id}`}
                >
                  <div className="min-w-[160px] text-xs text-white/50">
                    {formatDate(p.created_at)}
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-medium text-white capitalize">
                      {t(`billing.kind.${p.kind}`)}
                      {p.nxt_amount ? (
                        <span className="ml-2 text-[#D4AF37]">+{p.nxt_amount} NXT</span>
                      ) : null}
                    </div>
                    <div className="text-xs text-white/45">
                      {p.payment_method?.card?.brand
                        ? `${p.payment_method.card.brand} · •••• ${p.payment_method.card.last4}`
                        : p.payment_method?.source || p.payment_method?.type || ""}
                      {p.provider_intent_id ? (
                        <span className="ml-2 text-white/30">· {p.provider_intent_id.slice(0, 18)}…</span>
                      ) : null}
                    </div>
                  </div>
                  <div className="min-w-[90px] text-right text-sm font-semibold tabular-nums text-white">
                    {p.amount_cents > 0 ? formatPrice(p.amount_cents, p.currency) : "—"}
                  </div>
                  <StatusPill status={p.status} label={t(`billing.status.${p.status}`)} />
                </li>
              ))}
            </ul>
          )}
        </GlassCard>
      )}

      <WalletSyncFooter lastUpdated={lastUpdated} keyPath="billing.syncLine" />
    </section>
  );
}
